import { useQuery } from '@tanstack/react-query';
import {
  getNotes,
  searchNotes,
  getNote,
  getClientNotes,
} from '../services/notesApi';
import type { NoteFilters } from '../types/note';

// Query keys for caching
export const noteKeys = {
  all: ['notes'] as const,
  lists: () => [...noteKeys.all, 'list'] as const,
  list: (filters?: NoteFilters) => [...noteKeys.lists(), filters] as const,
  search: (query: string) => [...noteKeys.all, 'search', query] as const,
  details: () => [...noteKeys.all, 'detail'] as const,
  detail: (id: string) => [...noteKeys.details(), id] as const,
  client: (clientId: string) => [...noteKeys.all, 'client', clientId] as const,
};

/**
 * Hook to fetch notes with filters and pagination
 */
export const useNotes = (filters?: NoteFilters) => {
  return useQuery({
    queryKey: noteKeys.list(filters),
    queryFn: () => getNotes(filters),
  });
};

/**
 * Hook to search notes
 */
export const useNoteSearch = (query: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: noteKeys.search(query),
    queryFn: () => searchNotes(query),
    enabled: options?.enabled ?? (query.length >= 2),
  });
};

/**
 * Hook to fetch a single note
 */
export const useNote = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: noteKeys.detail(id),
    queryFn: () => getNote(id),
    enabled: options?.enabled ?? !!id,
  });
};

/**
 * Hook to fetch notes for a specific client
 */
export const useClientNotes = (clientId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: noteKeys.client(clientId),
    queryFn: () => getClientNotes(clientId),
    enabled: options?.enabled ?? !!clientId,
  });
};

export default useNotes;
